import { z } from 'zod';

/**
 * Vistoria digital (entrada e entrega do carro): a loja monta os modelos, marca foto ou
 * observação obrigatória por item, o cliente assina na tela e sai um PDF com as fotos.
 */

export const TIPOS_VISTORIA = ['entrada', 'saida'] as const;
export type TipoVistoria = (typeof TIPOS_VISTORIA)[number];
export const NOME_TIPO_VISTORIA: Record<TipoVistoria, string> = {
  entrada: 'Entrada (o carro chega na loja)',
  saida: 'Entrega (o carro sai para o cliente)',
};

export const itemModeloVistoriaSchema = z.object({
  descricao: z.string().trim().min(2, 'Descreva o item').max(80),
  fotoObrigatoria: z.boolean(),
  observacaoObrigatoria: z.boolean(),
});
export type ItemModeloVistoria = z.infer<typeof itemModeloVistoriaSchema>;

export const modeloVistoriaSalvarSchema = z.object({
  nome: z.string().trim().min(2, 'Dê um nome').max(60),
  tipo: z.enum(TIPOS_VISTORIA),
  ativo: z.boolean(),
  itens: z.array(itemModeloVistoriaSchema).min(1, 'Coloque ao menos um item').max(60),
});
export type ModeloVistoriaSalvar = z.infer<typeof modeloVistoriaSalvarSchema>;

const item = (descricao: string, foto = true, obs = false): ItemModeloVistoria => ({
  descricao,
  fotoObrigatoria: foto,
  observacaoObrigatoria: obs,
});
/** Modelos que a loja recebe no primeiro uso (ela edita à vontade). */
export const MODELOS_VISTORIA_PADRAO: ModeloVistoriaSalvar[] = [
  {
    nome: 'Vistoria de entrega',
    tipo: 'saida',
    ativo: true,
    itens: [
      item('Frente'),
      item('Traseira'),
      item('Lateral esquerda'),
      item('Lateral direita'),
      item('Para-brisa e vidros'),
      item('Pneus, rodas e estepe'),
      item('Interior e bancos'),
      item('Painel sem luzes de alerta'),
      item('Hodômetro (km)', true, true),
      item('Nível de combustível', true, false),
      item('Chave principal e reserva', false),
      item('Manual e documentos entregues', false),
    ],
  },
  {
    nome: 'Vistoria de entrada',
    tipo: 'entrada',
    ativo: true,
    itens: [
      item('Frente'),
      item('Traseira'),
      item('Lateral esquerda'),
      item('Lateral direita'),
      item('Riscos e amassados', true, false),
      item('Pneus e estepe'),
      item('Interior'),
      item('Hodômetro (km)', true, true),
      item('Chave reserva', false),
      item('Documento do carro (CRV/ATPV)', false),
    ],
  },
];

export const SITUACOES_ITEM_VISTORIA = ['ok', 'avaria', 'nao_se_aplica'] as const;
export type SituacaoItemVistoria = (typeof SITUACOES_ITEM_VISTORIA)[number];
export const NOME_SITUACAO_VISTORIA: Record<SituacaoItemVistoria, string> = {
  ok: 'OK',
  avaria: 'Com avaria',
  nao_se_aplica: 'Não se aplica',
};

export const vistoriaCriarSchema = z
  .object({
    modeloId: z.string().uuid(),
    veiculoId: z.string().uuid().optional(),
    clienteId: z.string().uuid().optional(),
    /** Carro que ainda não está no estoque (ex.: troca chegando) */
    placa: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{3}-?\d[A-Z0-9]\d{2}$/, 'Placa inválida')
      .transform((p) => p.replace('-', ''))
      .optional(),
    descricao: z.string().trim().min(2).max(120).optional(),
  })
  .refine((v) => v.veiculoId || (v.placa && v.descricao), {
    message: 'Escolha um carro do estoque ou informe placa e modelo',
    path: ['veiculoId'],
  });
export type VistoriaCriar = z.infer<typeof vistoriaCriarSchema>;

export const vistoriaSalvarSchema = z.object({
  km: z.number().int().min(0).max(2_000_000).nullable().optional(),
  itens: z
    .array(
      z.object({
        situacao: z.enum(SITUACOES_ITEM_VISTORIA).nullable(),
        observacao: z
          .string()
          .trim()
          .max(500)
          .transform((v) => v || null)
          .nullable(),
      }),
    )
    .max(60),
});
export type VistoriaSalvar = z.infer<typeof vistoriaSalvarSchema>;

/** Assinatura desenhada na tela: imagem PNG ou JPEG em data URL (até ~300 KB). */
export const vistoriaConcluirSchema = z.object({
  assinanteNome: z.string().trim().min(3, 'Nome de quem assina').max(120),
  assinatura: z
    .string()
    .max(400_000, 'Assinatura grande demais')
    .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/, 'Assinatura inválida'),
});
export type VistoriaConcluir = z.infer<typeof vistoriaConcluirSchema>;

export const vistoriaEmailSchema = z.object({
  para: z.string().trim().toLowerCase().email('E-mail inválido').max(200),
});

export interface ItemVistoria extends ItemModeloVistoria {
  situacao: SituacaoItemVistoria | null;
  observacao: string | null;
  fotos: string[];
}

/** O que falta para concluir (mesma regra na tela e na API). */
export function faltasVistoria(itens: ItemVistoria[]): string[] {
  const f: string[] = [];
  for (const i of itens) {
    if (!i.situacao) f.push(`${i.descricao}: marque a situação`);
    else if (i.situacao === 'nao_se_aplica') continue;
    else {
      if (i.fotoObrigatoria && !i.fotos.length) f.push(`${i.descricao}: falta a foto`);
      if ((i.observacaoObrigatoria || i.situacao === 'avaria') && !i.observacao?.trim())
        f.push(
          `${i.descricao}: ${i.situacao === 'avaria' ? 'descreva a avaria' : 'falta a observação'}`,
        );
    }
  }
  return f;
}

// ─────────── Link de avaliação (o cliente manda o carro antes de vir) ───────────

export const linkAvaliacaoCriarSchema = z.object({
  nome: z.string().trim().min(2, 'Nome do cliente').max(120),
  telefone: z
    .string()
    .trim()
    .max(30)
    .transform((v) => v || null)
    .nullable()
    .optional(),
  clienteId: z.string().uuid().optional(),
  origem: z.enum(['compra_pf', 'compra_loja', 'troca', 'consignacao']),
});
export type LinkAvaliacaoCriar = z.infer<typeof linkAvaliacaoCriarSchema>;

const anoAtual = () => new Date().getFullYear();
/** O que o cliente preenche no link (as fotos vão junto no mesmo envio). */
export const avaliacaoClienteSchema = z
  .object({
    placa: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{3}-?\d[A-Z0-9]\d{2}$/, 'Placa inválida')
      .transform((p) => p.replace('-', '')),
    marca: z.string().trim().min(2, 'Informe a marca').max(60),
    modelo: z.string().trim().min(1, 'Informe o modelo').max(80),
    versao: z
      .string()
      .trim()
      .max(120)
      .transform((v) => v || null)
      .nullable()
      .optional(),
    anoFabricacao: z.coerce
      .number()
      .int()
      .min(1950)
      .max(anoAtual() + 1),
    anoModelo: z.coerce
      .number()
      .int()
      .min(1950)
      .max(anoAtual() + 2),
    km: z.coerce.number().int().min(0).max(2_000_000),
    cor: z
      .string()
      .trim()
      .max(40)
      .transform((v) => v || null)
      .nullable()
      .optional(),
    valorPedidoCentavos: z.coerce.number().int().min(0).max(100_000_000_00).nullable().optional(),
    observacoes: z
      .string()
      .trim()
      .max(1000)
      .transform((v) => v || null)
      .nullable()
      .optional(),
  })
  .refine((c) => c.anoModelo === c.anoFabricacao || c.anoModelo === c.anoFabricacao + 1, {
    message: 'Ano modelo deve ser igual ou 1 a mais que o de fabricação',
    path: ['anoModelo'],
  });
export type AvaliacaoCliente = z.infer<typeof avaliacaoClienteSchema>;

// ─────────── Ordem de serviço (garantia e serviços no carro) ───────────

export const TIPOS_OS = ['garantia', 'cortesia', 'preparacao'] as const;
export type TipoOs = (typeof TIPOS_OS)[number];
export const NOME_TIPO_OS: Record<TipoOs, string> = {
  garantia: 'Garantia',
  cortesia: 'Cortesia ao cliente',
  preparacao: 'Preparação para venda',
};
export const STATUS_OS = ['aberta', 'no_servico', 'concluida', 'cancelada'] as const;
export type StatusOs = (typeof STATUS_OS)[number];
export const NOME_STATUS_OS: Record<StatusOs, string> = {
  aberta: 'Aberta',
  no_servico: 'No serviço',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

const itemOsSchema = z.object({
  descricao: z.string().trim().min(2, 'Descreva o serviço').max(120),
  valorCentavos: z.number().int().min(0).max(100_000_000),
});
export const osCriarSchema = z.object({
  veiculoId: z.string().uuid(),
  tipo: z.enum(TIPOS_OS),
  reclamacao: z.string().trim().min(3, 'Descreva o problema').max(1000),
  prestador: z
    .string()
    .trim()
    .max(120)
    .transform((v) => v || null)
    .nullable()
    .optional(),
});
export type OsCriar = z.infer<typeof osCriarSchema>;
export const osSalvarSchema = z.object({
  prestador: z
    .string()
    .trim()
    .max(120)
    .transform((v) => v || null)
    .nullable(),
  itens: z.array(itemOsSchema).max(40),
  observacoes: z
    .string()
    .trim()
    .max(2000)
    .transform((v) => v || null)
    .nullable(),
});
export type OsSalvar = z.infer<typeof osSalvarSchema>;
export const osEntradaSchema = z.object({
  km: z.number().int().min(0).max(2_000_000).nullable().optional(),
});
